import { ArrowLeftOnRectangleIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useNavigate } from "react-router-dom";

import LoadingWheel from "../components/shared/LoadingWheel";
import { AuthProvider, useAuth } from "../contexts/AuthContext";
import { PageNameProvider } from "../contexts/PageTitleContext";
import { ScannerProvider } from "../contexts/ScannerContext";
import useBrandTheme from "../hooks/useBrandTheme";

/**
 * Full-screen shell for the till: no topbar, no sidebar, big touch targets. Keeps the same
 * auth, page-title and scanner providers as the dashboard so pages behave identically, but
 * the scanner is mounted here because DashboardLayout (which normally mounts it) isn't.
 */
export default function KioskLayout() {
  return (
    <AuthProvider>
      <PageNameProvider>
        <ScannerProvider>
          <KioskShell>
            <Outlet />
          </KioskShell>
        </ScannerProvider>
      </PageNameProvider>
    </AuthProvider>
  );
}

function KioskShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("common");
  const { loading, name } = useAuth();
  const brand = useBrandTheme();
  const navigate = useNavigate();

  const logo = brand.data?.data?.croppedLogo ?? brand.data?.data?.logo;

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col bg-gray-50 select-none">
      <header className="h-14 shrink-0 flex items-center justify-between gap-4 px-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          {logo ? <img src={logo} alt="" className="h-8 w-auto max-w-[140px] object-contain" /> : null}
          <span className="text-lg font-semibold text-gray-900">{t("Till")}</span>
        </div>

        <div className="flex items-center gap-2">
          {name ? <span className="hidden sm:block text-sm text-gray-600 truncate max-w-[200px]">{name}</span> : null}
          <Link
            to="/help/till"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label={t("Help")}
          >
            <QuestionMarkCircleIcon className="h-6 w-6" />
          </Link>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            <span className="hidden sm:inline">{t("Exit till")}</span>
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0">{children}</main>
    </div>
  );
}
